import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Bot, Image, FileText, Sparkles, Zap, Crown, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/atoms/Button';
import { Card } from '../../components/atoms/Card';
import { Input } from '../../components/atoms/Input';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import { type UserSettings } from '../../types/database';

type PresetTone = 'cheap' | 'balanced' | 'premium';

interface ModelPreset {
  id: string;
  label: string;
  description: string;
  badge?: string;
  tone: PresetTone;
}

const TEXT_MODEL_PRESETS: ModelPreset[] = [
  {
    id: 'google/gemini-2.5-flash',
    label: 'Gemini 2.5 Flash',
    description: 'Brzo i jeftino — najbolja opcija za male budgete',
    badge: 'JEFTINO • PREPORUČENO',
    tone: 'cheap',
  },
  {
    id: 'anthropic/claude-haiku-4.5',
    label: 'Claude Haiku 4.5',
    description: 'Anthropic kvaliteta uz nižu cijenu',
    badge: 'JEFTINO',
    tone: 'cheap',
  },
  {
    id: 'google/gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    description: 'Bolja kvaliteta od Flash verzije',
    tone: 'balanced',
  },
  {
    id: 'anthropic/claude-sonnet-4.5',
    label: 'Claude Sonnet 4.5',
    description: 'Premium kvaliteta scenarija',
    badge: 'PREMIUM',
    tone: 'premium',
  },
  {
    id: 'anthropic/claude-sonnet-4.6',
    label: 'Claude Sonnet 4.6',
    description: 'Najnoviji Anthropic model',
    badge: 'NAJNOVIJE',
    tone: 'premium',
  },
];

const IMAGE_MODEL_PRESETS: ModelPreset[] = [
  {
    id: 'black-forest-labs/flux.2-klein-4b',
    label: 'Flux 2 Klein 4B',
    description: 'Najjeftinije i brzo, dovoljno za 90% scena',
    badge: 'JEFTINO • DEFAULT',
    tone: 'cheap',
  },
  {
    id: 'google/gemini-2.5-flash-image',
    label: 'Gemini 2.5 Flash Image',
    description: 'Konzistentne scene kroz više kadrova',
    tone: 'balanced',
  },
  {
    id: 'black-forest-labs/flux.2-max',
    label: 'Flux 2 Max',
    description: 'Maksimalna kvaliteta',
    badge: 'PREMIUM',
    tone: 'premium',
  },
];

const TONE_STYLES: Record<PresetTone, { ring: string; badgeBg: string; badgeText: string; icon: React.ReactNode }> = {
  cheap: {
    ring: 'ring-green-500/40',
    badgeBg: 'bg-green-500/15',
    badgeText: 'text-green-400',
    icon: <DollarSign size={12} />,
  },
  balanced: {
    ring: 'ring-blue-500/40',
    badgeBg: 'bg-blue-500/15',
    badgeText: 'text-blue-400',
    icon: <Zap size={12} />,
  },
  premium: {
    ring: 'ring-purple-500/40',
    badgeBg: 'bg-purple-500/15',
    badgeText: 'text-purple-400',
    icon: <Crown size={12} />,
  },
};

interface ModelPickerProps {
  presets: ModelPreset[];
  value: string;
  onChange: (modelId: string) => void;
}

function ModelPicker({ presets, value, onChange }: ModelPickerProps) {
  const matchedPreset = presets.find((p) => p.id === value);
  const [isCustom, setIsCustom] = useState(!matchedPreset);

  useEffect(() => {
    setIsCustom(!presets.find((p) => p.id === value));
  }, [value, presets]);

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {presets.map((preset) => {
          const isActive = !isCustom && value === preset.id;
          const tone = TONE_STYLES[preset.tone];
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                setIsCustom(false);
                onChange(preset.id);
              }}
              className={`text-left p-3 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-all ${
                isActive ? `ring-2 ${tone.ring} bg-white/[0.06]` : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-medium text-white">{preset.label}</span>
                {preset.badge && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${tone.badgeBg} ${tone.badgeText} flex items-center gap-1`}>
                    {tone.icon}
                    {preset.badge}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 leading-snug">{preset.description}</p>
              <p className="text-[10px] text-slate-600 mt-1 font-mono truncate">{preset.id}</p>
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => setIsCustom(true)}
          className={`text-left p-3 rounded-xl border border-dashed border-white/15 bg-white/[0.01] hover:bg-white/[0.04] transition-all ${
            isCustom ? 'ring-2 ring-slate-400/40 bg-white/[0.06]' : ''
          }`}
        >
          <span className="text-sm font-medium text-slate-300">Custom...</span>
          <p className="text-xs text-slate-500 leading-snug">Unesi vlastiti model ID s OpenRouter-a</p>
        </button>
      </div>

      {isCustom && (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="npr. anthropic/claude-sonnet-4.5"
        />
      )}
    </div>
  );
}

const DEFAULT_FULL_SCRIPT_MODEL = 'google/gemini-2.5-flash';
const DEFAULT_SCRIPT_MODEL = 'google/gemini-2.5-flash';
const DEFAULT_IMAGE_MODEL = 'black-forest-labs/flux.2-klein-4b';

export function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [fullScriptModel, setFullScriptModel] = useState(DEFAULT_FULL_SCRIPT_MODEL);
  const [scriptModel, setScriptModel] = useState(DEFAULT_SCRIPT_MODEL);
  const [imageModel, setImageModel] = useState(DEFAULT_IMAGE_MODEL);

  useEffect(() => {
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          const { data: newSettings, error: createError } = await supabase
            .from('user_settings')
            .insert({ user_id: user.id })
            .select()
            .single();

          if (createError) throw createError;
          setSettings(newSettings);
          setFullScriptModel(newSettings.generate_full_script_model || DEFAULT_FULL_SCRIPT_MODEL);
          setScriptModel(newSettings.generate_script_model || DEFAULT_SCRIPT_MODEL);
          setImageModel(newSettings.generate_image_model || DEFAULT_IMAGE_MODEL);
        } else {
          throw error;
        }
      } else {
        setSettings(data);
        setFullScriptModel(data.generate_full_script_model || DEFAULT_FULL_SCRIPT_MODEL);
        setScriptModel(data.generate_script_model || DEFAULT_SCRIPT_MODEL);
        setImageModel(data.generate_image_model || DEFAULT_IMAGE_MODEL);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      toast.error('Greška pri učitavanju postavki');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user || !settings) return;

    if (!fullScriptModel.trim() || !scriptModel.trim() || !imageModel.trim()) {
      toast.error('Sva polja modela moraju biti popunjena');
      return;
    }

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('user_settings')
        .update({
          generate_full_script_model: fullScriptModel.trim(),
          generate_script_model: scriptModel.trim(),
          generate_image_model: imageModel.trim(),
        })
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Postavke su uspješno spremljene');
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error('Greška pri spremanju postavki');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Učitavanje postavki...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/projects')}
              leftIcon={<ArrowLeft size={18} />}
            >
              Natrag
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-600 rounded-xl">
                <Settings size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-white uppercase tracking-tighter">
                  Postavke
                </h1>
                <p className="text-sm text-slate-500">
                  Konfiguracija AI modela
                </p>
              </div>
            </div>
          </div>

          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            leftIcon={<Sparkles size={18} />}
          >
            Spremi Promjene
          </Button>
        </div>

        <div className="space-y-6">
          <Card variant="glass" padding="lg" radius="2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <FileText size={20} className="text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Modeli za Generiranje Teksta</h2>
                <p className="text-sm text-slate-500">Odaberite AI modele za generiranje i poboljšanje scenarija</p>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Generiranje Kompletnog Scenarija
                </label>
                <ModelPicker
                  presets={TEXT_MODEL_PRESETS}
                  value={fullScriptModel}
                  onChange={setFullScriptModel}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Poboljšanje Scenarija
                </label>
                <ModelPicker
                  presets={TEXT_MODEL_PRESETS}
                  value={scriptModel}
                  onChange={setScriptModel}
                />
              </div>
            </div>
          </Card>

          <Card variant="glass" padding="lg" radius="2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Image size={20} className="text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Modeli za Generiranje Slika</h2>
                <p className="text-sm text-slate-500">Odaberite AI model za generiranje vizuala</p>
              </div>
            </div>

            <ModelPicker
              presets={IMAGE_MODEL_PRESETS}
              value={imageModel}
              onChange={setImageModel}
            />
          </Card>

          <Card variant="glass" padding="lg" radius="2xl" className="bg-blue-500/5 border-blue-500/20">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-500/20 rounded-lg shrink-0">
                <Bot size={20} className="text-blue-400" />
              </div>
              <div>
                <h3 className="font-medium text-white mb-2">O AI Modelima</h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  Ako imaš ograničen budget na OpenRouter-u, koristi <strong className="text-green-400">Gemini 2.5 Flash</strong> — brz je i najjeftiniji za scenarije.
                  Za premium kvalitetu odaberi <strong className="text-purple-400">Claude Sonnet 4.5/4.6</strong>.
                  Za slike <strong className="text-green-400">Flux 2 Klein 4B</strong> je default i pokriva većinu slučajeva.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
